import {Inject, Injectable} from '@angular/core';
import {
    BaseItem,
    BaseItemRequestPayload,
    GenericItem,
    Item,
} from '../models/item.model';
import {ApiUrl} from '@emm/core/util/api-url.token';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {standardRetry} from '@emm/core/util/rx.util';
import {
    AssignmentProperties,
    CreateVariation,
    Variation,
    VariationAssignment,
    VariationTemplate
} from '@emm/common-items/models/variation.model';
import {ModifierGroup} from '@emm/common-items/models/modifierGroup.model';
import {Modifier} from '@emm/common-items/models/modifier.model';

@Injectable()
export class ItemDataService {
    private all: GenericItem[] = [];

    constructor(
        @Inject(ApiUrl) private apiUrl: string,
        private http: HttpClient,
    ) {}

    getItems(effectiveDate?: string): Observable<BaseItem[]> {
        let url = `${this.apiUrl}/common-items?countryCode=US`;
        if (effectiveDate) {
            url += `&effectiveDate=${effectiveDate}`;
        }
        return this.http.get(url).pipe(
            map((response: any) => response.commonItems),
            standardRetry()
        );
    }

    getItem(tag: string, effectiveDate?: string): Observable<Item> {
        let url = `${this.apiUrl}/common-items/${tag}?countryCode=US`;
        if (effectiveDate) {
            url += `&effectiveDate=${effectiveDate}`;
        }
        return this.http.get(url).pipe(
            map((response: any) => response.response),
            standardRetry()
        );
    }

    createItem(item: BaseItemRequestPayload, requestedBy: string): Observable<Item> {
        const url = `${this.apiUrl}/common-items`;
        return this.http.post(url, { ...item, requestedBy }).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    toggleItemDisabled(tag: string, effectiveDate?: string): Observable<Item> {
        // TODO: Implement correct body
        let url = `${this.apiUrl}/common-items/${tag}/disabled`;
        if (effectiveDate) {
            url += `?effectiveDate=${effectiveDate}`;
        }
        return this.http.put(url, {}).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    updateItem(tag: string, updates: any, effectiveDate: string, requestedBy: string): Observable<Item> {
        const url = `${this.apiUrl}/common-items/${tag}?effectiveDate=${effectiveDate}`;
        return this.http.put(url, { ...updates, requestedBy }).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    addItemComponents(itemTag: string, components: any[], effectiveDate: string, requestedBy: string):
        Observable<Item | ModifierGroup | Modifier> {
        const url = `${this.apiUrl}/common-items/${itemTag}/components?effectiveDate=${effectiveDate}`;
        return this.http.put(url,
            {
                components,
                countryCode: 'US',
                language: 'en',
                requestedBy
            }).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    deleteItem(tag: string): Observable<any> {
        // TODO: Implement delete URL
        const url = `${this.apiUrl}/common-items/${tag}`;
        return this.http.delete(url).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    getModifierGroups(effectiveDate?: string): Observable<BaseItem[]> {
        let url = `${this.apiUrl}/modifier-groups?countryCode=US`;
        if (effectiveDate) {
            url += `&effectiveDate=${effectiveDate}`;
        }
        return this.http.get(url).pipe(
            map((response: any) => response.modifierGroups),
            standardRetry()
        );
    }

    getModifierGroup(tag: string, effectiveDate?: string): Observable<ModifierGroup> {
        let url = `${this.apiUrl}/modifier-groups/${tag}`;
        if (effectiveDate) {
            url += `?effectiveDate=${effectiveDate}`;
        }
        return this.http.get(url).pipe(
            map((response: any) => response.response),
            standardRetry()
        );
    }

    addModifierGroup(modifierGroup: any, requestedBy: string): Observable<ModifierGroup> {
        const url = `${this.apiUrl}/modifier-groups`;
        return this.http.post(url, { ...modifierGroup, requestedBy }).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    updateModifierGroup(tag: string, updates: any, effectiveDate: string, requestedBy: string): Observable<ModifierGroup> {
        const url = `${this.apiUrl}/modifier-groups/${tag}?effectiveDate=${effectiveDate}`;
        return this.http.put(url, { ...updates, requestedBy }).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    deleteModifierGroup(tag: string): Observable<any> {
        // TODO: Implement delete URL
        const url = `${this.apiUrl}/modifier-groups/${tag}`;
        return this.http.delete(url).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    getModifiers(effectiveDate?: string): Observable<BaseItem[]> {
        let url = `${this.apiUrl}/modifiers?countryCode=US`;
        if (effectiveDate) {
            url += `&effectiveDate=${effectiveDate}`;
        }
        return this.http.get(url).pipe(
            map((response: any) => response.modifiers),
            standardRetry()
        );
    }

    getModifier(tag: string, effectiveDate?: string): Observable<Modifier> {
        let url = `${this.apiUrl}/modifiers/${tag}`;
        if (effectiveDate) {
            url += `?effectiveDate=${effectiveDate}`;
        }
        return this.http.get(url).pipe(
            map((response: any) => response.response),
            standardRetry()
        );
    }

    addModifier(modifier: Modifier, requestedBy: string): Observable<ModifierGroup> {
        const url = `${this.apiUrl}/modifiers`;
        return this.http.post(url, { ...modifier, requestedBy }).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    updateModifier(tag: string, updates: any, effectiveDate: string, requestedBy: string): Observable<Modifier> {
        const url = `${this.apiUrl}/modifiers/${tag}?effectiveDate=${effectiveDate}`;
        return this.http.put(url, { ...updates, requestedBy }).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    deleteModifier(tag: string): Observable<any> {
        // TODO: Implement delete URL
        const url = `${this.apiUrl}/modifiers/${tag}`;
        return this.http.delete(url).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    getBaseItemTemplate(currentClass: string): Observable<VariationTemplate> {
        const url = `${this.apiUrl}/item-variations/templates/base/${currentClass}`;
        return this.http.get(url).pipe(
            map((response: any) => response.templates),
            standardRetry()
        );
    }

    getVariationTemplates(itemClass: string, locationScope?: string): Observable<VariationTemplate[]> {
        let url = `${this.apiUrl}/item-variations/templates?itemClass=${itemClass}`;
        if (locationScope) {
            url += `?locationScope=${locationScope}`;
        }
        return this.http.get(url).pipe(
            map((response: any) => response.templates),
            standardRetry()
        );
    }

    getItemVariations(tag: string, effectiveDate?: string): Observable<Variation[]> {
        let url = `${this.apiUrl}/item-variations/${tag}`;
        if (effectiveDate) {
            url += `?effectiveDate=${effectiveDate}`;
        }
        return this.http.get(url).pipe(
            map((response: any) => response.itemVariations),
            standardRetry()
        );
    }

    createItemVariation(newVariation: CreateVariation, requestedBy: string): Observable<Variation> {
        const url = `${this.apiUrl}/item-variations`;
        return this.http.post(url, { ...newVariation, requestedBy }).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    updateItemVariation(variationTag: string, updates: any, effectiveDate: string, requestedBy: string): Observable<Variation> {
        variationTag = encodeURIComponent(variationTag);
        const url = `${this.apiUrl}/item-variations/${variationTag}?effectiveDate=${effectiveDate}`;
        return this.http.put(url, { ...updates, requestedBy }).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    deleteItemVariation(variationTag: string, effectiveDate: string, requestedBy: string): Observable<any> {
        variationTag = encodeURIComponent(variationTag);
        const url = `${this.apiUrl}/item-variations/${variationTag}?effectiveDate=${effectiveDate}`;
        return this.http.delete(url, { body: { requestedBy } }).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    getContextAssignments(tag: string, effectiveDate?: string): Observable<VariationAssignment> {
        let url = `${this.apiUrl}/variation-assignments/${tag}`;
        if (effectiveDate) {
            url += `?effectiveDate=${effectiveDate}`;
        }
        return this.http.get(url).pipe(
            map((response: any) => response.variationAssignments),
            standardRetry()
        );
    }

    getAllowedAssignmentProperties(): Observable<AssignmentProperties> {
        const url = `${this.apiUrl}/variation-assignments/properties`;
        return this.http.get(url).pipe(
            map((response: any) => response.propertyDetails),
            standardRetry()
        );
    }

    updateContextAssignment(effectiveDate: string, payload: {
        requestedBy: string;
        assignments: Array<{
            hash?: string;
            locationId: string;
            consumer: string;
            variationTag: string;
            itemTag: string;
            properties: any;
        }>;
    }): Observable<VariationAssignment> {
        const url = `${this.apiUrl}/variation-assignments?effectiveDate=${effectiveDate}`;
        return this.http.put(url, payload).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }

    deleteContextAssignment(tag: string, context: any, effectiveDate: string, requestedBy: string): Observable<any> {
        const url = `${this.apiUrl}/variation-assignments/${tag}?effectiveDate=${effectiveDate}`;
        return this.http.delete(url, { body: { ...context, requestedBy } }).pipe(
            map((response: any) => response),
            standardRetry()
        );
    }
}
